import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { ChevronRight, ExternalLink, Loader2, Megaphone, Plus } from 'lucide-react'
import { toast } from 'sonner'
import {
  createAnnouncement,
  listAnnouncements,
} from '../../../gen/cortado/v1/admin-AdminService_connectquery'
import {
  CreateAnnouncementRequestSchema,
  ListAnnouncementsRequestSchema,
  type Announcement,
} from '../../../gen/cortado/v1/announcements_pb'
import { AnnouncementDisplayTypeEnum } from '../../../gen/cortado/v1/settings_pb'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import { Textarea } from '../../../components/ui/textarea'
import { HeaderImagePicker } from '../../../components/announcements/header-image-picker'
import { formatTimestamp, displayTypeLabel } from './-utils'

export const Route = createFileRoute('/_admin/announcements/')({
  component: AnnouncementsPage,
})

const announcementListKey = createConnectQueryKey({
  schema: listAnnouncements,
  cardinality: undefined,
})

function AnnouncementsPage() {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [displayType, setDisplayType] = useState<AnnouncementDisplayTypeEnum>(
    AnnouncementDisplayTypeEnum.CARD,
  )
  const [primaryButtonLabel, setPrimaryButtonLabel] = useState('')
  const [primaryButtonValue, setPrimaryButtonValue] = useState('')
  const [secondaryButtonLabel, setSecondaryButtonLabel] = useState('')
  const [secondaryButtonValue, setSecondaryButtonValue] = useState('')
  const [headerImageBase64, setHeaderImageBase64] = useState<string | null>(null)
  const [tag, setTag] = useState('')

  const { data, isLoading } = useQuery(
    listAnnouncements,
    create(ListAnnouncementsRequestSchema, { limit: 50 }),
  )

  const announcements = data?.announcements ?? []

  const createMutation = useMutation(createAnnouncement, {
    onSuccess: async () => {
      setTitle('')
      setBody('')
      setDisplayType(AnnouncementDisplayTypeEnum.CARD)
      setPrimaryButtonLabel('')
      setPrimaryButtonValue('')
      setSecondaryButtonLabel('')
      setSecondaryButtonValue('')
      setHeaderImageBase64(null)
      setTag('')
      toast.success('Announcement published')
      await queryClient.invalidateQueries({ queryKey: announcementListKey })
    },
    onError: (error) => {
      toast.error(error.message || 'Unable to publish announcement')
    },
  })

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const trimmedPrimaryLabel = primaryButtonLabel.trim()
    const trimmedPrimaryValue = primaryButtonValue.trim()
    const trimmedSecondaryLabel = secondaryButtonLabel.trim()
    const trimmedSecondaryValue = secondaryButtonValue.trim()

    if ((trimmedPrimaryLabel && !trimmedPrimaryValue) || (!trimmedPrimaryLabel && trimmedPrimaryValue)) {
      toast.error('Primary button requires both a label and URL/path')
      return
    }
    if (trimmedSecondaryValue && !trimmedSecondaryLabel) {
      toast.error('Secondary button URL/path requires a label')
      return
    }

    createMutation.mutate(
      create(CreateAnnouncementRequestSchema, {
        title,
        body,
        displayType,
        primaryButtonLabel: trimmedPrimaryLabel || undefined,
        primaryButtonValue: trimmedPrimaryValue || undefined,
        secondaryButtonLabel: trimmedSecondaryLabel || undefined,
        secondaryButtonValue: trimmedSecondaryValue || undefined,
        tag: tag.trim() || undefined,
        headerImageBase64: headerImageBase64 ?? undefined,
      }),
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Announcements</h1>
        <p className="text-sm text-muted-foreground">
          Publish dashboard announcements to every organization.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-muted-foreground" />
              New announcement
            </CardTitle>
            <CardDescription>Published announcements appear immediately.</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <Label>Header image (optional)</Label>
                <HeaderImagePicker base64={headerImageBase64} onChange={setHeaderImageBase64} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="announcement-title">Title</Label>
                <Input
                  id="announcement-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  maxLength={160}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="announcement-tag">Tag (optional)</Label>
                <Input
                  id="announcement-tag"
                  value={tag}
                  onChange={(event) => setTag(event.target.value)}
                  maxLength={80}
                  placeholder="Private Beta"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="announcement-body">Body</Label>
                <Textarea
                  id="announcement-body"
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  rows={5}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="announcement-display-type">Presentation</Label>
                <Select
                  value={String(displayType)}
                  onValueChange={(value) => setDisplayType(Number(value) as AnnouncementDisplayTypeEnum)}
                >
                  <SelectTrigger id="announcement-display-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(AnnouncementDisplayTypeEnum.CARD)}>Card</SelectItem>
                    <SelectItem value={String(AnnouncementDisplayTypeEnum.MODAL)}>Modal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="announcement-primary-button-label">Primary button label</Label>
                  <Input
                    id="announcement-primary-button-label"
                    value={primaryButtonLabel}
                    onChange={(event) => setPrimaryButtonLabel(event.target.value)}
                    placeholder="Open"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="announcement-primary-button-value">Primary URL/path</Label>
                  <Input
                    id="announcement-primary-button-value"
                    value={primaryButtonValue}
                    onChange={(event) => setPrimaryButtonValue(event.target.value)}
                    placeholder="/settings or https://example.com"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="announcement-secondary-button-label">Secondary button label</Label>
                  <Input
                    id="announcement-secondary-button-label"
                    value={secondaryButtonLabel}
                    onChange={(event) => setSecondaryButtonLabel(event.target.value)}
                    placeholder="Dismiss"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="announcement-secondary-button-value">Secondary URL/path</Label>
                  <Input
                    id="announcement-secondary-button-value"
                    value={secondaryButtonValue}
                    onChange={(event) => setSecondaryButtonValue(event.target.value)}
                    placeholder="/dashboard"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                Publish
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Published announcements</CardTitle>
            <CardDescription>
              Click an announcement to view read analytics.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading announcements...</p>
            ) : announcements.length === 0 ? (
              <p className="text-sm text-muted-foreground">No announcements published yet.</p>
            ) : (
              <div className="divide-y rounded-md border">
                {announcements.map((announcement) => (
                  <AnnouncementListItem key={announcement.id} announcement={announcement} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function AnnouncementListItem({ announcement }: { announcement: Announcement }) {
  const primaryLabel = announcement.primaryButtonLabel?.trim()
    || (announcement.actionUrl ? 'Open' : '')
  const secondaryLabel = announcement.secondaryButtonLabel?.trim() || ''
  const isArchived = Boolean(announcement.archivedAt)

  return (
    <Link
      to="/announcements/$announcementId"
      params={{ announcementId: announcement.id }}
      className={`block space-y-2 p-4 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        isArchived ? 'opacity-60' : ''
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold leading-5">{announcement.title}</h2>
            {isArchived && <Badge variant="outline">Archived</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">
            Published {formatTimestamp(announcement.publishedAt)}
          </p>
        </div>
        <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
          <Badge variant={announcement.displayType === AnnouncementDisplayTypeEnum.MODAL ? 'info' : 'secondary'}>
            {displayTypeLabel(announcement.displayType)}
          </Badge>
          {primaryLabel && (
            <Badge variant="outline" className="max-w-full">
              <ExternalLink className="h-3 w-3" />
              <span className="truncate">Primary: {primaryLabel}</span>
            </Badge>
          )}
          {secondaryLabel && (
            <Badge variant="outline" className="max-w-full">
              <span className="truncate">Secondary: {secondaryLabel}</span>
            </Badge>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      <p className="whitespace-pre-wrap text-sm text-muted-foreground">{announcement.body}</p>
    </Link>
  )
}
